import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { User, UserCollection } from '../_models';
import { ContactMessage } from '../_models/contactMessage';
import { Conversation, ConversationCollection } from '../_models/conversation';
import { Crush, CrushCollection } from '../_models/crush';
import { HeaderSummary } from '../_models/headerSummary';
import { ModelCollection } from '../_models/hydra';
import { Message, MessageCollection } from '../_models/message';
import { Picture } from '../_models/picture';
import { PictureAccessRequest, PictureAccessRequestCollection } from '../_models/pictureAccessRequest';
import { Room, RoomCollection } from '../_models/room';
import { ShoutBoxMessage } from '../_models/shoutBoxMessage';
import { UserFavoriteCollection } from '../_models/userFavorite';
import { VisitCollection } from '../_models/visit';
import { StatsSummary } from '../_models/statsSummary';
import { WidgetMessageCollection } from '../_models/widgetMessage';
import { UserFavoriteRoomCollection } from '../_models/userFavoriteRoom';
import { PaymentCollection } from '../_models/payment';
import { CardCollection } from '../_models/card';

@Injectable()
export class ApiService {
    
    /**
     * Constructor
     */
    constructor(private http: HttpClient) {}

    /**
     * Login a user
     */
    login(username: string, password: string) {
        return this.http.post<any>(
            `${environment.apiUrl}/login_check`,
            { username, password },
            { withCredentials: true }
        );
    }

    /**
     * Refresh user token
     */
    refreshToken(refresh_token: string) {
        return this.http.post<any>(
            `${environment.apiUrl}/token/refresh`,
            { refresh_token: refresh_token },
            { withCredentials: true }
        );
    }

    /**
     * Revoke user token
     */
    revokeToken() {
        return this.http.post<any>(
            `${environment.apiUrl}/token/revoke`,
            {},
            { withCredentials: true }
        );
    }

    /**
     * Get user favorites
     */
    getFavorites(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<UserFavoriteCollection>(
            `${environment.apiUrl}/user_favorites`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user favorite rooms
     */
    getFavoriteRooms(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<UserFavoriteRoomCollection>(
            `${environment.apiUrl}/user_favorite_rooms`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get header data summary
     */
    getHeaderSummary() {
        return this.http.get<HeaderSummary>(
            `${environment.apiUrl}/header_summary`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get stats data summary
     */
    getStatsSummary() {
        return this.http.get<StatsSummary>(
            `${environment.apiUrl}/stats_summary`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get user sent crushes
     */
    getSentCrushes(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<CrushCollection>(
            `${environment.apiUrl}/crushes/sents`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user received crushes
     */
    getReceivedCrushes(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<CrushCollection>(
            `${environment.apiUrl}/crushes/received`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user accepted crushes
     */
    getAcceptedCrushes(
        count: number,
        page: number = 1,
        partial: boolean = false
    ) {
        return this.http.get<CrushCollection>(
            `${environment.apiUrl}/crushes/accepted`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user sent crushes
     */
    getSentorAcceptedCrushes(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<CrushCollection>(
            `${environment.apiUrl}/crushes/sents-or-accepted`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user suggestions
     */
    getSuggestions(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<UserCollection>(
            `${environment.apiUrl}/users/suggests`,
            {
                params: {
                partial: partial,
                itemsPerPage: count,
                page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get online users
     */
    getOnlines(users: Array<string>, features:Array<string> = ['global']) {
        let usersId: Array<string> = users.map(id => id.replace(/\/users\//, ''));
        return this.http.post<{ [id: string]: boolean }>(
            `${environment.apiUrl}/users/online`,
            {
                features: features,
                userIds: usersId
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get new users
     */
    getNewbies(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<UserCollection>(`${environment.apiUrl}/users/new`, {
            params: {
                partial: partial,
                itemsPerPage: count,
                page: page
            },
            withCredentials: true
        });
    }

    /**online
     * Get new users
     */
    getOnlineUsers(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<UserCollection>(`${environment.apiUrl}/users/online`, {
            params: {
                partial: partial,
                itemsPerPage: count,
                page: page
            },
            withCredentials: true
        });
    }

    /**
     * Get user conversations
     */
    getConversations(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<ConversationCollection>(
            `${environment.apiUrl}/conversations`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user conversations
     */
    getUnreadConversations() {
        return this.http.get<ConversationCollection>(
            `${environment.apiUrl}/conversations/unread`,
            {
                params: {
                    
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get conversation with user
     */
    getUserConversation(id: string) {
        return this.http.post<Conversation>(
            `${environment.apiUrl}/conversations`,
            {
                members: [id]
            },
            {
                withCredentials: true
            }
        );
    }
    
    /**
     * Delete conversation
     */
    deleteConversation(conversationId: string) {
        return this.http.delete<any>(
            `${environment.apiUrl}${conversationId}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get a room
     */
    getRoom(id: string) {
        return this.http.get<Room>(
            `${environment.apiUrl}${id}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Enter a room
     */
    enterRoom(id: string) {
        return this.http.post<void>(
            `${environment.apiUrl}${id}/enter`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get pending authorisation
     */
    getRoomOnlines(roomId:string, count: number = 1000, page: number = 1) {
        return this.http.get<UserCollection>(`${environment.apiUrl}/users/connected/shout_box-${roomId}`, {
            params: {
                itemsPerPage: count,
                page: page
            },
            withCredentials: true
        });
    }

    /**
     * Get a conversation
     */
    getConversation(id: string) {
        return this.http.get<Conversation>(
            `${environment.apiUrl}${id}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set conversation as read
     */
    touchConversation(id: string) {
        return this.http.post<null>(
            `${environment.apiUrl}${id}/touch`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get new visits
     */
    getNewVisits() {
        return this.http.get<VisitCollection>(`${environment.apiUrl}/visits/new`, {
            params: {},
            withCredentials: true
        });
    }

    /**
     * Get user visits
     */
    getVisits(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<VisitCollection>(`${environment.apiUrl}/visits`, {
            params: {
                partial: partial,
                itemsPerPage: count,
                page: page
            },
            withCredentials: true
        });
    }

    /**
     * Get user by username
     */
    getUserByUsername(username: string) {
        return this.http.get<User>(
            `${environment.apiUrl}/users/by_username/${username}`,
                {
                    withCredentials: true
                }
            );
    }

    /**
     * Get user private access
     */
     getPrivatePictureAccess(userId: string) {
        return this.http.get<PictureAccessRequest>(
            `${environment.apiUrl}${userId}/picture_access_request`,
                {
                    withCredentials: true
                }
            );
    }

    /**
     * Add user to favorites
     */
    toggleFavorites(userId: string) {
        return this.http.post<User>(
            `${environment.apiUrl}${userId}/favorite`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send crush to user
     */
    toggleCrush(userId: string) {
        return this.http.post<Crush>(
            `${environment.apiUrl}/crushes`,
            {
                target: userId
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get conversation messages
     */
    getMessages(convoId: string, count: number, maxRank:number = 2147483647) {
        return this.http.get<MessageCollection>(
            `${environment.apiUrl}${convoId}/messages`,
            {
                params: {
                    itemsPerPage: count,
                    "rank[lt]": maxRank
                },
                withCredentials: true
            }
        );
    }

    /**
     * Send message
     */
    sendMessage(convoId: string, text: string) {
        return this.http.post<Message>(
            `${environment.apiUrl}/messages`,
            {
                conversation: convoId,
                message: text
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send message reply
     */
    sendReply(convoId: string, messageId:string, text: string) {
        return this.http.post<Message>(
            `${environment.apiUrl}${messageId}/reply`,
            {
                conversation: convoId,
                message: text
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send message reply
     */
    sendRoomReply(roomId:string, messageId:string, text: string) {
        return this.http.post<Message>(
            `${environment.apiUrl}${messageId}/reply`,
            {
                room: roomId,
                message: text
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send reaction
     */
    sendReaction(messageId: string, reaction: string) {
        return this.http.post<Message>(
            `${environment.apiUrl}${messageId}/reaction`,
            {
                reaction: reaction
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Delete reaction
     */
    deleteReaction(messageId: string, reaction: string) {
        return this.http.delete<Message>(
            `${environment.apiUrl}${messageId}/reaction`,
            {
                withCredentials: true,
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                body: {
                    reaction: reaction
                }
            }
        );
    }

    /**
     * Send room message
     */
    sendRoomMessage(roomId: string, text: string) {
        return this.http.post<Message>(
            `${environment.apiUrl}/shout_box_messages`,
            {
                room: roomId,
                message: text
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send image message
     */
     sendImageMessage(convoId: string, blob: any) {
        let formData = new FormData();
        formData.append("image", blob);

        return this.http.post<Message>(
            `${environment.apiUrl}${convoId}/upload_image`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send video message
     */
    sendVideoMessage(convoId: string, blob: any) {
        let formData = new FormData();
        formData.append("video", blob);

        return this.http.post<Message>(
            `${environment.apiUrl}${convoId}/upload_video`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send record message
     */
    sendRecordMessage(convoId: string, blob: any, mime: string, duration: number) {
        let formData = new FormData();
        formData.append("record", blob);
        formData.append("mime", mime);
        formData.append("duration", duration.toString());

        return this.http.post<Message>(
            `${environment.apiUrl}${convoId}/upload_record`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Upload an image
     */
    uploadImage(blob: any, priv:boolean = false) {
        let formData = new FormData();
        formData.append("file", blob);
        formData.append("private", priv ? "true" : "false");
        formData.append("targetProfilePicture", priv ? "false" : "true");

        return this.http.post<Picture>(
            `${environment.apiUrl}/pictures`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Delete image
     */
    deleteImage(imageId:string) {
        return this.http.delete<any>(
            `${environment.apiUrl}${imageId}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send image message to room
     */
    sendRoomImageMessage(roomId: string, blob: any) {
        let formData = new FormData();
        formData.append("image", blob);

        return this.http.post<Message>(
            `${environment.apiUrl}${roomId}/shout_box_messages/upload_image`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send video message to room
     */
    sendRoomVideoMessage(roomId: string, blob: any) {
        let formData = new FormData();
        formData.append("video", blob);

        return this.http.post<Message>(
            `${environment.apiUrl}${roomId}/shout_box_messages/upload_video`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Delete message
     */
    deleteMessage(messageId: string) {
        return this.http.delete<any>(
            `${environment.apiUrl}${messageId}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Edit message (Geek only, owner only)
     */
    editMessage(messageId: string, text: string) {
        return this.http.put<Message>(
            `${environment.apiUrl}${messageId}/edit`,
            {
                message: text
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set user position
     */
    setUserPosition(userId: string, latitude: number, longitude: number) {
        let lastKnownPosition = null;
        
        if (latitude || longitude) {
            lastKnownPosition = {
                latitude: latitude,
                longitude: longitude
            }
        }

        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            {
                lastKnownPosition: lastKnownPosition
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set localization
     */
    setLocalizationAuthorisation(userId: string, value: boolean) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            {
                allowLocalization: value
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Move a favorite right before another one, or last when before is null
     *
     * The API cannot place a favorite from an index of the displayed list: it holds
     * favorites that list never shows, the ones pointing at a deleted or locked user.
     */
    setFavoriteRank(favId: string, before: string) {
        return this.http.put<any>(
            `${environment.apiUrl}${favId}/set_rank`,
            {
                before: before
            },
            {
                withCredentials: true
            }
        );
    }


    /**
     * Set image rank
     */
     setImageRank(imageId: string, rank: number) {
        return this.http.put<any>(
            `${environment.apiUrl}${imageId}`,
            {
                rank: rank
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send contact message
     */
    sendContactMessage(content: string, topic: string) {
        return this.http.post<ContactMessage>(
            `${environment.apiUrl}/contact_messages`,
            {
                content: content
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get contact link
     */
    getContactLink(type: string, context: string) {
        return this.http.post<ContactMessage>(
            `${environment.apiUrl}/message/chatroom/url`,
            {
                context: context
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Send invitation
     */
    sendInvitation(userId: string, email: string) {
        let body = new FormData();
        body.append('email', email);
        return this.http.post<ContactMessage>(
            `${environment.apiUrl}/invite`,
            body,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Update user address
     */
    updateUserAddress(userId:string, address: any) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            {
                address: address
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Update user data
     */
    updateUserData(userId:string, data: any) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            data,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set profil picture
     */
    setProfilPicture(userId:string, pictureId:string) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            {
                profilePicture: pictureId
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Request access private album
     */
    requestAccessPrivateAlbum(userId:string) {
        return this.http.post<any>(
            `${environment.apiUrl}/picture_access_requests`,
            {
                user: userId
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get searches
     */
    getSearches() {
        return this.http.get<any>(
            `${environment.apiUrl}/searches`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Save search
     */
    saveSearch(data:any) {
        return this.http.post<any>(
            `${environment.apiUrl}/searches`,
            {
                criteria: data,
                name: new Date().toISOString()
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Lock user
     */
    lockUser(userId:string) {
        return this.http.post<any>(
            `${environment.apiUrl}${userId}/lock`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Unlock user
     */
    unlockUser(userId:string) {
        return this.http.post<any>(
            `${environment.apiUrl}${userId}/unlock`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set notif
     */
    setNotifs(userId:string, notificationMailTopics:string[], notificationPushTopics:string[]) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            {
                notificationMailTopics: notificationMailTopics,
                notificationPushTopics: notificationPushTopics
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set visibility
     */
    setVisibility(userId:string, status:string) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}/set_invisibility/${status}`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Set visibility
     */
    setAdminVisibility(userId:string, status:string) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}/set_fully_invisibility/${status}`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Update user
     */
    updateUser(userId:string, data:any) {
        return this.http.put<any>(
            `${environment.apiUrl}${userId}`,
            data,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get pending authorisation
     */
    getPendingAuthorisations(count: number = 20, page: number = 1) {
        return this.http.get<PictureAccessRequestCollection>(`${environment.apiUrl}/picture_access_requests/list/to_process`, {
            params: {
                itemsPerPage: count,
                page: page
            },
            withCredentials: true
        });
    }

    /**
     * Delete authorisation
     */
    deletePictureAccessRequest(requestId: string) {
        return this.http.delete<any>(
            `${environment.apiUrl}${requestId}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get locked users
     */
    getLocked(count: number = 20, page: number = 1) {
        return this.http.get<ModelCollection<User>>(`${environment.apiUrl}/users/locked`, {
            params: {
                itemsPerPage: count,
                page: page
            },
            withCredentials: true
        });
    }

    /**
     * Set authorisation status
     */
    setRequestStatus(requestId: string, status: string) {
        return this.http.put<PictureAccessRequest>(
            `${environment.apiUrl}${requestId}`,
            {
                status: status
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Delete search
     */
    deleteSearch(searchId:string) {
        return this.http.delete<any>(
            `${environment.apiUrl}${searchId}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get search results
     */
    getSearchResults(terms:any, count: number, page: number = 1, partial: boolean = false) {
        let params = {
            partial: partial,
            itemsPerPage: count,
            page: page,
            ...terms
        }

        const searchParams = (data) => {
            const params = new URLSearchParams();
          
            for (let key in data) {
                if (Array.isArray(data[key])) {
                    for (let val of data[key]) {
                        params.append(key + '[]', val);
                    }
                    map(data[key], item => params.append(key + '[]', item));
                  } else {
                    params.append(key, data[key]);    
                  }
            }
          
            return params;
          };

        return this.http.get<UserCollection>(
            `${environment.apiUrl}/users/search?${searchParams(params)}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Delete
     */
    delete(userId:string, reason:string) {
        return this.http.delete<void>(`${environment.apiUrl}${userId}/delete?reason=${reason}`, {
            withCredentials: true
        },);
    }

    /**
     * Suspend
     */
    suspend(userId:string, reason:string) {
        return this.http.delete<void>(`${environment.apiUrl}${userId}/suspend?reason=${reason}`,{
            withCredentials: true
        });
    }

    /**
     * Advertise typing
     */
    advertiseTyping(conversationId:string) {
        return this.http.post<void>(`${environment.apiUrl}${conversationId}/i_am_typing`, {
            withCredentials: true
        });
    }

    /**
     * Set mobile notification Id
     */
    setDeviceId(deviceId:string, deviceType:string) {
        return this.http.post<void>(`${environment.apiUrl}/notification_devices`, 
        {
            deviceId: deviceId,
            deviceType: deviceType
        },
        {
            withCredentials: true
        });
    }

    /**
     * Set mobile notification Id
     */
    deleteDeviceId(deviceId:string) {
        return this.http.delete<void>(`${environment.apiUrl}/notification_devices/${deviceId}`, 
        {
            withCredentials: true
        });
    }

    /**
     * Unsubscribe to all email
     */
    unsubscribeMails(email:string, token:string) {
        return this.http.post<void>(`${environment.apiUrl}/unsubscribe-emails`, 
        {
            email: email,
            token: token
        },
        {
            withCredentials: true
        });
    }

    /**
     * Subscribe to all email
     */
    subscribeAllMails(email:string, token:string) {
        return this.http.post<void>(`${environment.apiUrl}/subscribe-emails-all`, 
        {
            email: email,
            token: token
        },
        {
            withCredentials: true
        });
    }

    /**
     * Validate email
     */
    validateEmail(email:string, code:string) {
        return this.http.get<void>(`${environment.apiUrl}/email_verification`, 
        {
            params: {
                email: email,
                code: code
            },
            withCredentials: true
        });
    }

    /**
     * Validate email
     */
    validatePhone(code:string, token) {
        return this.http.get<void>(`${environment.apiUrl}/phone_verification`, 
        {
            params: {
                code: code
            },
            headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
            withCredentials: true
        });
    }

    /**
     * Resend validation code
     */
    resendValidationCode(userId, token){
        return this.http.get<void>(`${environment.apiUrl}${userId}/resend_validation_code`, 
        {
            params: {},
            headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
            withCredentials: true
        });
    }

    /**
     * Get page content
     */
    getPage(slug){
        return this.http.get<void>(`${environment.apiUrl}/pages/by_reference/${slug}`, 
        {
            params: {},
            withCredentials: true
        });
    }

    /**
     * Get widget content
     */
    getWidget(id){
        return this.http.get<void>(`${environment.apiUrl}/widget_messages/${id}`, 
        {
            params: {},
            withCredentials: true
        });
    }

    /**
     * Get widget messages
     */
    getWidgetMessages(count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<WidgetMessageCollection>(
            `${environment.apiUrl}/widget_messages`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get rooms
     */
    getRooms(type: string, count: number, page: number = 1, partial: boolean = false) {
        return this.http.get<RoomCollection>(
            `${environment.apiUrl}/rooms/${type}`,
            {
                params: {
                    partial: partial,
                    itemsPerPage: count,
                    page: page
                },
                withCredentials: true
            }
        );
    }

    /**
     * Create room
     */
    createRoom(type: string, name: string, description: string, blob: any, members: Array<string>) {
        let formData = new FormData();
        formData.append("image", blob);
        formData.append("type", type);
        formData.append("name", name);
        formData.append("members", members.join(','));
        formData.append("description", description);

        return this.http.post<Room>(
            `${environment.apiUrl}/rooms`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Delete room
     */
    deleteRoom(roomId: string) {
        return this.http.delete<any>(
            `${environment.apiUrl}${roomId}`,
            {
                withCredentials: true
            }
        );
    }

    /**
     * Leave room
     */
    leaveRoom(roomId: string) {
        return this.http.post<any>(
            `${environment.apiUrl}${roomId}/leave`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Leave room
     */
    updateMembers(roomId: string, members:string[]) {
       return this.http.put<any>(
            `${environment.apiUrl}${roomId}`,
            {
                members: members
            },
            {
                withCredentials: true
            }
        );
    }

    /**
     * Leave room
     */
    updateRoom(roomId: string, data:any) {
        let formData = new FormData();
        if (data.name) {
            formData.append("name", data.name);
        }
        if (data.description) {
            formData.append("description", data.description);
        }
        if (data.image) {
            formData.append("image", data.image);
        }
        if (data.members) {
            formData.append("members", data.members);
        }
        
        return this.http.post<any>(
             `${environment.apiUrl}${roomId}/update`,
             formData,
             {
                 withCredentials: true
             }
         );
    }

    /**
     * Add room to favorites
     */
    toggleRoomFavorites(roomId: string) {
        return this.http.post<Room>(
            `${environment.apiUrl}${roomId}/favorite`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * MArk widget messages as read
     */
    markWidgetMessagesAsRead() {
        return this.http.post<void>(
            `${environment.apiUrl}/widget_messages/read`,
            {},
            {
                withCredentials: true
            }
        );
    }

    /**
     * Get user receipts
     */
    getReceipts() {
        return this.http.get<PaymentCollection>(
            `${environment.apiUrl}/payments`,
            {
                params: {
                    
                },
                withCredentials: true
            }
        );
    }

    /**
     * Get user cards
     */
    getCards() {
        return this.http.get<CardCollection>(
            `${environment.apiUrl}/cards`,
            {
                params: {
                    
                },
                withCredentials: true
            }
        );
    }
}
